"use client";
import * as React from "react";
import Link from "next/link";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { WarningCircleIcon } from "@phosphor-icons/react/dist/csr/WarningCircle";
import { InfoIcon } from "@phosphor-icons/react/dist/csr/Info";
import { CircleNotchIcon } from "@phosphor-icons/react/dist/csr/CircleNotch";
import { XIcon } from "@phosphor-icons/react/dist/csr/X";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

/**
 * Meldingen rechtsboven, onder de kop, als stapel (docs/toast.md). Overal aan te roepen, ook buiten React:
 *
 *   toast.success("Vak toegevoegd")
 *   toast.error("Opslaan lukte niet", { description: "Controleer je verbinding.", action: { label: "Opnieuw", onClick: save } })
 *   toast.promise(upload(), { loading: "Uploaden…", success: "Geüpload", error: "Uploaden lukte niet" })
 *
 * Een fout blijft staan tot je hem wegklikt; de rest gaat na een paar seconden weg, behalve zolang je erop wijst.
 * De meldingen van ‘Op de achtergrond’ staan in dezelfde kolom (`ToastHost`) en gebruiken dezelfde kaart (`ToastCard`).
 */

export type ToastKind = "success" | "error" | "warning" | "info" | "loading" | "default";
export type ToastAction = { label: string; onClick?: () => void; href?: string };
export type ToastOptions = {
  /** Zelfde id = dezelfde melding bijwerken in plaats van een nieuwe erbij (bijv. ‘Opslaan…’ → ‘Opgeslagen’). */
  id?: string;
  description?: React.ReactNode;
  action?: ToastAction;
  /** Tweede, rustige knop, zoals ‘Later’. */
  cancel?: ToastAction;
  /** In milliseconden; `Infinity` = blijft staan. Standaard 5 s, een fout en ‘bezig’ blijven staan. */
  duration?: number;
};
type ToastData = ToastOptions & { id: string; kind: ToastKind; title: React.ReactNode; leaving?: boolean };

const DURATION = 5000, LEAVE = 250, MAX = 4;
let toasts: ToastData[] = [];
let counter = 0;
const listeners = new Set<() => void>();
const emit = () => listeners.forEach(l => l());

function show(kind: ToastKind, title: React.ReactNode, options: ToastOptions = {}) {
  const id = options.id ?? `t${++counter}`;
  const next: ToastData = { ...options, id, kind, title };
  const at = toasts.findIndex(t => t.id === id);
  toasts = at >= 0 ? toasts.map((t, i) => (i === at ? next : t)) : [...toasts, next].slice(-MAX);
  emit();
  return id;
}

function dismiss(id?: string) {
  const leaving = toasts.filter(t => id == null || t.id === id);
  if (!leaving.length) return;
  toasts = toasts.map(t => (leaving.includes(t) ? { ...t, leaving: true } : t));
  emit();
  setTimeout(() => { toasts = toasts.filter(t => !leaving.some(l => l.id === t.id) || !t.leaving); emit(); }, LEAVE);
}

type Message<T> = React.ReactNode | ((value: T) => React.ReactNode);
const resolve = <T,>(m: Message<T>, v: T) => (typeof m === "function" ? (m as (value: T) => React.ReactNode)(v) : m);

export const toast = Object.assign((title: React.ReactNode, options?: ToastOptions) => show("default", title, options), {
  success: (title: React.ReactNode, options?: ToastOptions) => show("success", title, options),
  error: (title: React.ReactNode, options?: ToastOptions) => show("error", title, options),
  warning: (title: React.ReactNode, options?: ToastOptions) => show("warning", title, options),
  info: (title: React.ReactNode, options?: ToastOptions) => show("info", title, options),
  loading: (title: React.ReactNode, options?: ToastOptions) => show("loading", title, options),
  /** ‘Bezig’ zolang de belofte loopt, daarna dezelfde melding als gelukt of mislukt. Geeft de belofte terug. */
  promise<T>(promise: Promise<T>, m: { loading: React.ReactNode; success: Message<T>; error: Message<unknown> } & Omit<ToastOptions, "id">) {
    const { loading, success, error, ...options } = m;
    const id = show("loading", loading, options);
    promise.then(v => show("success", resolve(success, v), { ...options, id }), e => show("error", resolve(error, e), { ...options, id }));
    return promise;
  },
  dismiss,
});

function useToasts() {
  return React.useSyncExternalStore(
    cb => { listeners.add(cb); return () => listeners.delete(cb); },
    () => toasts,
    () => EMPTY,
  );
}
const EMPTY: ToastData[] = [];

/* ── Weergave ───────────────────────────────────────────────────────── */

const icons: Record<Exclude<ToastKind, "default">, { icon: React.ElementType; className: string; weight?: "bold" | "fill" }> = {
  success: { icon: CheckIcon, weight: "bold", className: "bg-emerald-500/12 text-(--tag-green-ink) ring-emerald-500/25" },
  error: { icon: WarningCircleIcon, weight: "fill", className: "bg-destructive/12 text-destructive ring-destructive/25" },
  warning: { icon: WarningIcon, weight: "fill", className: "bg-amber-500/12 text-(--tag-amber-ink) ring-amber-500/25" },
  info: { icon: InfoIcon, weight: "fill", className: "bg-primary/12 text-(--tag-blue-ink) ring-primary/25" },
  loading: { icon: CircleNotchIcon, weight: "bold", className: "bg-muted text-muted-foreground ring-foreground/10 [&>svg]:animate-spin" },
};

/** Het icoon links in een melding, per soort. */
export function ToastIcon({ kind }: { kind: ToastKind }) {
  if (kind === "default") return null;
  const { icon: Icon, className, weight } = icons[kind];
  return <span aria-hidden className={cn("grid size-8 shrink-0 place-items-center rounded-lg ring-1", className)}><Icon weight={weight} className="size-4" /></span>;
}

/**
 * De kaart van één melding. Gaat na `duration` weg, behalve zolang je erop wijst, de stapel open staat of er het toetsenbord in staat.
 * Ook voor meldingen met een eigen icoon of eigen knoppen (‘Op de achtergrond’): geef dan `icon` en `actions` mee.
 */
export function ToastCard({ title, description, icon, actions, alert, duration, leaving, onDismiss, className }: {
  title: React.ReactNode; description?: React.ReactNode; icon?: React.ReactNode; actions?: React.ReactNode;
  alert?: boolean; duration: number; leaving?: boolean; onDismiss: () => void; className?: string;
}) {
  const [paused, setPaused] = React.useState(false);
  const held = React.useContext(HeldContext);
  const close = React.useRef(onDismiss);
  const ref = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => { close.current = onDismiss; });
  React.useEffect(() => {
    if (paused || held || leaving || !Number.isFinite(duration)) return;
    const t = setTimeout(() => close.current(), duration);
    return () => clearTimeout(t);
  }, [paused, held, leaving, duration]);
  // Binnenkomen: eerst de beginstand vastleggen (reflow), dan loslaten in dezelfde taak. Zie .t-stack-toast in globals.css.
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.dataset.enter = "";
    void el.offsetWidth;
    delete el.dataset.enter;
  }, []);
  return (
    <div ref={ref} role={alert ? "alert" : "status"} data-toast="" data-leaving={leaving || undefined}
      onPointerEnter={() => setPaused(true)} onPointerLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setPaused(false); }}
      className={cn("t-stack-toast rounded-2xl border border-edge-925 bg-popover shadow-modal", className)}>
      <div data-toast-body="" className="flex gap-3 p-3.5">
        {icon}
        <div className="min-w-0 flex-1 self-center">
          <p className="text-sm font-medium">{title}</p>
          {description && <div className="mt-0.5 text-[13px] text-muted-foreground">{description}</div>}
          {actions && <div className="mt-2.5 flex flex-wrap gap-2">{actions}</div>}
        </div>
        <button type="button" onClick={onDismiss} aria-label="Melding sluiten" className="-mt-1 -mr-1 grid size-7 shrink-0 place-items-center self-start rounded-lg text-muted-foreground hover:bg-muted hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring outline-none">
          <XIcon size={14} />
        </button>
      </div>
    </div>
  );
}

function ActionButton({ action, variant, onDone }: { action: ToastAction; variant?: "outline"; onDone: () => void }) {
  const run = () => { action.onClick?.(); onDone(); };
  if (action.href) return <Button asChild size="sm" variant={variant}><Link href={action.href} onClick={run}>{action.label}</Link></Button>;
  return <Button size="sm" variant={variant} onClick={run}>{action.label}</Button>;
}

/* ── De kolom ───────────────────────────────────────────────────────── */

const HostContext = React.createContext<HTMLElement | null>(null);
/** Staat de stapel open (wijzen, aantikken of toetsenbord), dan wacht elke melding met weggaan. */
const HeldContext = React.createContext(false);

/** De plek waar andere meldingen (‘Op de achtergrond’) hun kaarten in zetten, in dezelfde stapel als de gewone meldingen. */
export function useToastHost() { return React.useContext(HostContext); }

const px = (el: Element, name: string, fallback: number) => {
  const v = parseFloat(getComputedStyle(el).getPropertyValue(name));
  return Number.isFinite(v) ? v : fallback;
};

/**
 * Stapelt alle kaarten in de kolom (ook die van ‘Op de achtergrond’, via een portal): de nieuwste vooraan,
 * oudere schuiven kleiner en lichter naar achteren. Open (`spread`) staan ze onder elkaar.
 * Werkt op de DOM, zodat kaarten uit elke bron meedoen; de beweging zelf staat in CSS (.t-stack-toast).
 */
function watchStack(stack: HTMLElement, read: () => { spread: boolean; onEmpty: () => void }) {
  const born = new WeakMap<Element, number>();
  const watched = new Set<Element>();
  let n = 0;
  const layout = () => {
    const cards = [...stack.querySelectorAll<HTMLElement>("[data-toast]")];
    for (const c of cards) if (!born.has(c)) born.set(c, ++n);
    for (const el of watched) if (!el.isConnected) { resize.unobserve(el); watched.delete(el); }
    for (const c of cards) { const body = c.firstElementChild; if (body && !watched.has(body)) { resize.observe(body); watched.add(body); } }
    const live = cards.filter(c => c.dataset.leaving == null).sort((a, b) => born.get(b)! - born.get(a)!);
    const { spread: open, onEmpty } = read();
    if (!live.length) { stack.style.height = "0px"; onEmpty(); return; }
    const peek = px(stack, "--stack-peek", 12), gap = px(stack, "--stack-spread-gap", 8);
    const shrink = px(stack, "--stack-depth-scale", 0.06), fade = px(stack, "--stack-depth-fade", 0.4);
    const heights = live.map(c => (c.firstElementChild as HTMLElement).offsetHeight + c.offsetHeight - c.clientHeight);
    let y = 0;
    live.forEach((c, i) => {
      const back = !open && i > 0;
      c.style.height = `${back ? heights[0] : heights[i]}px`;
      c.style.zIndex = String(live.length - i);
      c.style.setProperty("--y", `${open ? y : i * peek}px`);
      c.style.setProperty("--s", String(open ? 1 : 1 - i * shrink));
      c.style.setProperty("--o", String(open ? 1 : i > 2 ? 0 : 1 - [0, 1, 1.6][i] * fade));
      c.toggleAttribute("data-back", back);
      y += heights[i] + gap;
    });
    stack.style.height = `${open ? y - gap : heights[0] + Math.min(live.length - 1, 2) * peek}px`;
  };
  const resize = new ResizeObserver(layout);
  const mutations = new MutationObserver(layout);
  mutations.observe(stack, { subtree: true, childList: true, attributes: true, attributeFilter: ["data-leaving"] });
  layout();
  return { layout, stop: () => { mutations.disconnect(); resize.disconnect(); } };
}

function useStack(stack: HTMLElement | null, spread: boolean, onEmpty: () => void) {
  const latest = React.useRef({ spread, onEmpty });
  const watcher = React.useRef<ReturnType<typeof watchStack> | null>(null);
  React.useEffect(() => { latest.current = { spread, onEmpty }; watcher.current?.layout(); }, [spread, onEmpty]);
  React.useEffect(() => {
    if (!stack) return;
    const w = watchStack(stack, () => latest.current);
    watcher.current = w;
    return () => { w.stop(); watcher.current = null; };
  }, [stack]);
}

/**
 * Eén stapel voor alle meldingen, rechtsboven onder de kop. Wijs erop (of tik erop) en hij klapt open.
 * Hij schuift mee met een vastgezette coach of mail (`--dock-slot`, gezet door `AppShell`).
 * Eén keer neerzetten, rond de app (`StudyShell`).
 */
export function Toaster({ children }: { children?: React.ReactNode }) {
  const list = useToasts();
  const [host, setHost] = React.useState<HTMLElement | null>(null);
  const [stack, setStack] = React.useState<HTMLElement | null>(null);
  const [spread, setSpread] = React.useState(false);
  const collapse = React.useCallback(() => setSpread(false), []);
  useStack(stack, spread, collapse);
  // Op een aanraakscherm klapt aantikken hem open; tikken ernaast klapt hem weer dicht.
  React.useEffect(() => {
    if (!spread || !stack) return;
    const outside = (e: PointerEvent) => { if (!stack.contains(e.target as Node)) setSpread(false); };
    document.addEventListener("pointerdown", outside);
    return () => document.removeEventListener("pointerdown", outside);
  }, [spread, stack]);
  return (
    <HostContext.Provider value={host}>
      <HeldContext.Provider value={spread}>
        {children}
        <div role="region" aria-label="Meldingen" style={{ right: "calc(12px + var(--dock-slot, 0px))" }}
          className="pointer-events-none fixed top-[calc(var(--h-kop)+0.5rem)] z-50 w-[min(24rem,calc(100vw-1.5rem))] transition-[right] duration-300 ease-out print:hidden max-sm:top-[4.75rem]">
          <div ref={setStack} className="t-stack pointer-events-auto"
            onPointerEnter={e => { if (e.pointerType === "mouse") setSpread(true); }}
            onPointerLeave={e => { if (e.pointerType === "mouse") setSpread(false); }}
            onPointerDown={e => { if (e.pointerType !== "mouse") setSpread(true); }}
            onFocus={() => setSpread(true)} onBlur={e => { if (!e.currentTarget.contains(e.relatedTarget)) setSpread(false); }}>
            <div ref={setHost} className="contents" />
            {list.map(t => (
              <ToastCard key={t.id} title={t.title} description={t.description} icon={<ToastIcon kind={t.kind} />} leaving={t.leaving}
                alert={t.kind === "error"} duration={t.duration ?? (t.kind === "error" || t.kind === "loading" ? Infinity : DURATION)}
                onDismiss={() => dismiss(t.id)}
                actions={(t.action || t.cancel) && <>
                  {t.action && <ActionButton action={t.action} onDone={() => dismiss(t.id)} />}
                  {t.cancel && <ActionButton action={t.cancel} variant="outline" onDone={() => dismiss(t.id)} />}
                </>} />
            ))}
          </div>
        </div>
      </HeldContext.Provider>
    </HostContext.Provider>
  );
}
